import { useCallback, useRef, useState } from "react";
import { buildOptions, mapPanel } from "../../utils/gridstack";
import { GridStack } from "gridstack";
import { GridStackContext } from "./context";

export function GridStackProvider({ children }) {
  const [options, setOptions] = useState(null);
  const [instance, setInstance] = useState(null);
  const [panels, setPanels] = useState({});
  const containerByPanel = useRef(new Map());
  const container = useRef(null);
  const onAddPanel = useRef(() => console.warn("Not implemented"));
  const onUpdatePanel = useRef(() => console.warn("Not implemented"));
  const onDeletePanel = useRef(() => console.warn("Not implemented"));
  const onResetTabContent = useRef(() => console.warn("Not implemented"));

  const getPanelContainer = useCallback(
    (panelId) => containerByPanel.current.get(panelId) || null,
    []
  );

  const renderCB = useCallback((element, panel) => {
    if (panel.id) containerByPanel.current.set(panel.id, element);
  }, []);

  const onUpdateGridStackPanel = (_e, el) => {
    const items = el.gridstackNode.grid.getGridItems();
    const panels = items.map((item) => mapPanel(item.gridstackNode));
    onUpdatePanel.current(panels);
  };

  const startGridstack = useCallback(
    (options2, panelMap) => {
      if (!container.current) return null;

      if (instance) {
        safeTeardown(instance); // << SIEMPRE limpia todo antes
      }
      GridStack.renderCB = renderCB;
      const grid = GridStack.init(options2, container.current);

      grid.on("dragstop", onUpdateGridStackPanel);
      grid.on("resizestop", onUpdateGridStackPanel);

      Object.values(panelMap).forEach((panel) => {
        if (!containerByPanel.current.has(panel.id)) {
          grid.addWidget({ ...panel }); // asegúrate de pasar x,y,w,h si tu build no los autogenera
        }
      });

      setInstance(grid);
      setOptions(options2);
      setPanels(panelMap);
    },
    [renderCB, onUpdateGridStackPanel]
  );

  const safeTeardown = (grid) => {
    if (!grid) return;
    try {
      // 1) desuscribir eventos
      grid.off?.("dragstop", onUpdateGridStackPanel);
      grid.off?.("resizestop", onUpdateGridStackPanel);

      // 2) limpiar SIEMPRE todos los paneles del DOM
      if (grid.engine) {
        // removeAll(removeDOM=true, triggerEvent=false)
        grid.engine.removeAll(true, false);
      } else if (container.current) {
        // fallback si engine ya no existe
        container.current
          .querySelectorAll(".grid-stack-item")
          .forEach((el) => el.remove());
      }

      // 3) destruir SIN eliminar el contenedor .grid-stack
      grid.destroy(false);
    } catch (e) {
      console.error("safeTeardown error:", e);
    } finally {
      containerByPanel.current.clear();
    }
  };
  const onPanelsFetch = (newPanels) => {
    const options = buildOptions(newPanels);
    const panelMap = newPanels.reduce(
      (map, panel) => ({ ...map, [panel.id]: panel }),
      {}
    );
    startGridstack(options, panelMap);
  };

  const onCreateFirstPanel = async () => {
    const panel = await onAddPanel.current();
    const options = buildOptions([panel]);
    const panelMap = { [panel.id]: panel };
    startGridstack(options, panelMap);
  };

  const onAddGridStackPanel = async () => {
    if (!instance) return;
    const panel = await onAddPanel.current();
    console.log("gridstack provider onAddGridstackPanel", panel);
    instance.addWidget({ ...panel });
    setPanels((map) => ({ ...map, [panel.id]: panel }));
  };

  const onAddExistingPanelToGridStack = (panel) => {
    console.log("onAddExistingPanelToGridStack called with:", panel);
    console.log("GridStack instance exists:", !!instance);
    console.log("Current panels state:", panels);

    if (!instance) {
      console.warn("GridStack instance not available");
      return;
    }

    try {
      console.log("Adding widget to GridStack...");
      instance.addWidget({ ...panel });
      console.log("Widget added successfully");

      console.log("Updating panels state...");
      setPanels((map) => {
        const newMap = { ...map, [panel.id]: panel };
        console.log("New panels state:", newMap);
        return newMap;
      });
      console.log("Panels state updated");
    } catch (error) {
      console.error("Error adding panel to GridStack:", error);
    }
  };

  const onDeleteGridStackPanel = async (panelId, tableId) => {
    if (!instance) return;
    const isLast = Object.keys(panels).length === 1;

    instance.removeWidget(containerByPanel.current.get(panelId).parentNode);
    setPanels((map) => {
      const newMap = { ...map };
      delete newMap[panelId];
      return newMap;
    });
    containerByPanel.current.delete(panelId);
    await onDeletePanel.current(panelId, tableId);
    if (isLast) {
      onResetTabContent.current();
    }
  };

  const updatePanelData = useCallback((panelId, newData) => {
    setPanels((currentPanels) => {
      const panel = currentPanels[panelId];
      if (!panel) {
        console.warn(`Panel ${panelId} not found`);
        return currentPanels;
      }

      try {
        // Parsear el contenido actual del panel
        const currentContent = JSON.parse(panel.content);

        // Actualizar los props con los nuevos datos
        const updatedContent = {
          ...currentContent,
          props: {
            ...currentContent.props,
            // Solo actualizar data si se proporciona, preservar data existente si no
            data:
              newData.data !== undefined
                ? newData.data
                : currentContent.props.data,
            // Solo actualizar metadata si se proporciona, preservar metadata existente si no
            metadata:
              newData.metadata !== undefined
                ? newData.metadata
                : currentContent.props.metadata,
            // Agregar estado de loading para filtros
            isLoadingFilters:
              newData.isLoadingFilters !== undefined
                ? newData.isLoadingFilters
                : currentContent.props.isLoadingFilters || false,
            auxComparisonData: newData?.auxComparisonData
              ? newData.auxComparisonData
              : currentContent.props.auxComparisonData,
          },
        };

        // Crear el panel actualizado
        const updatedPanel = {
          ...panel,
          content: JSON.stringify(updatedContent),
        };

        console.log(`Panel ${panelId} data updated successfully`);

        return {
          ...currentPanels,
          [panelId]: updatedPanel,
        };
      } catch (error) {
        console.error(`Error updating panel ${panelId} data:`, error);
        return currentPanels;
      }
    });
  }, []);

  const contextValue = {
    instance: {
      ref: container,
      value: instance,
      getPanelContainer,
    },
    panels,
    updatePanelData,
    events: {
      onPanelsFetch,
      onAddPanel: onAddGridStackPanel,
      onAddExistingPanel: onAddExistingPanelToGridStack,
      onCreateFirstPanel,
      onDeletePanel: onDeleteGridStackPanel,
      set: {
        onAddPanel: (fn) => {
          onAddPanel.current = fn;
        },
        onUpdatePanel: (fn) => {
          onUpdatePanel.current = fn;
        },
        onDeletePanel: (fn) => {
          onDeletePanel.current = fn;
        },
        onResetTabContent: (fn) => {
          onResetTabContent.current = fn;
        },
      },
    },
  };

  return (
    <GridStackContext.Provider value={contextValue}>
      {children}
    </GridStackContext.Provider>
  );
}
